import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Sidenavaction} from '../../../../../assets/js/application/sidenavaction';
import {BusinessEntityService} from '../../../../services/businessEntity.service';
import {FarmSiteService} from '../../../../services/farmSite.service';
import {PondSectionService} from '../../../../services/pondSection.service';
import {CustomerPondService} from '../../../../services/customerPond.service';
import BusinessEntity from '../../../../services/businessEntity.model';
import FarmSiteModel from '../../../../services/farmSite.model';
import PondSectionModel from '../../../../services/pondSection.model';
import CustomerPondModel from '../../../../services/customerPond.model';
import {FeedLogService} from '../../../../services/feedLog.service';
import FeedLogModel from '../../../../services/feedLog.model';
import * as moment from 'moment';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * TankshiftComponent
 *
 * This component is responsible for managing the transfer of animals (tank shift) between ponds/tanks
 * within the Admin Panel. It provides a user interface for selecting source and destination tanks,
 * specifying the number of animals to shift, and executing the transfer operation.
 *
 * Key Responsibilities:
 * - Facilitate the transfer of animals from one tank (source) to another (destination).
 * - Validate transfer conditions (e.g., source tank must have animals, shift count <= source count).
 * - Calculate and display transfer percentages.
 * - Handle role-based access control for viewing and managing tank shifts.
 * - Manage UI state for form steps, confirmations, and loading indicators.
 *
 * User Roles:
 * - Farmer: Can manage tank shifts for their own farm.
 * - Farm Manager: Can manage tank shifts for assigned farms.
 * - Business Admin (Farm): Can manage tank shifts for their farm business.
 * - Business Admin (Provider): Can manage tank shifts for provider-managed farms.
 *
 * Features:
 * - **Tank Transfer**: Move animals between tanks with partial or full shift options.
 * - **Validation**: Ensures valid source/destination selection and animal counts.
 * - **Dynamic Dropdowns**: Loads farm sites, sections, and ponds based on selection.
 * - **Percentage Calculation**: Automatically calculates the percentage of animals being shifted.
 * - **Confirmation Step**: Requires user confirmation before finalizing the shift.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Loads business and farm site data based on user role.
 * 3. User selects business (if applicable) and farm site.
 * 4. User selects source and destination sections and tanks.
 * 5. User enters shift count; component validates and calculates percentage.
 * 6. User confirms shift; `shift()` calls `FeedLogService.tankShift` to execute transfer.
 *
 * @component
 * @selector app-tankshift
 * @example
 * <app-tankshift></app-tankshift>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-tankshift',
  templateUrl: './tankshift.component.html',
  styleUrls: ['./tankshift.component.scss']
})
export class TankshiftComponent implements OnInit, OnDestroy {
  /**
   * Array of business entities available for selection.
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * Array of farm sites associated with the selected business.
   * @type {Array<FarmSiteModel>}
   */
  public farmSites: Array<FarmSiteModel>;

  /**
   * Array of pond sections available in the selected farm site.
   * @type {Array<PondSectionModel>}
   */
  public pondSections: Array<PondSectionModel>;

  /**
   * Array of customer ponds (source candidates).
   * @type {Array<CustomerPondModel>}
   */
  public customerPonds: Array<CustomerPondModel>;

  /**
   * Array of customer ponds (destination candidates).
   * @type {Array<CustomerPondModel>}
   */
  public descustomerPonds: Array<CustomerPondModel>;

  /**
   * Array of all ponds in the selected farm site.
   * @type {Array<CustomerPondModel>}
   */
  public siteBycustomerPonds: Array<CustomerPondModel>;

  /**
   * Array of destination pond sections.
   * @type {Array<PondSectionModel>}
   */
  public destinationpondSections: Array<PondSectionModel>;

  /**
   * Array of source pond sections.
   * @type {Array<PondSectionModel>}
   */
  public sourceSections: Array<PondSectionModel>;

  /**
   * Array of feed logs (not actively used in current logic).
   * @type {Array<FeedLogModel>}
   */
  public feedLogs: Array<FeedLogModel>;

  /**
   * Model for the current tank shift operation (using FeedLogModel structure).
   * @type {FeedLogModel}
   */
  public feedLog: FeedLogModel;

  /**
   * Response model from the tank shift operation.
   * @type {FeedLogModel}
   */
  public responseFeedLog: FeedLogModel;

  /**
   * Role of the logged-in user.
   * @type {string}
   */
  public loginPersonType: string;

  /**
   * Name of the logged-in user.
   * @type {string}
   */
  public LoginUserName: string;

  /**
   * Business name of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessName: string;

  /**
   * Business category of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessCategory: string;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  public responseLoader: boolean;

  /**
   * Flag to show/hide the 'Go To' button (step navigation).
   * @type {boolean}
   */
  public gotoButton: boolean;

  /**
   * Flag to display tank selection fields.
   * @type {boolean}
   */
  public displayTanks: boolean;

  /**
   * Flag to disable download functionality (if applicable).
   * @type {boolean}
   */
  public disableDownload: boolean;

  /**
   * Flag to disable the commit button.
   * @type {boolean}
   */
  public isCommitDisabled: boolean;

  /**
   * Flag to indicate if the user is in the confirmation step.
   * @type {boolean}
   */
  public goToConfirm: boolean;

  /**
   * Flag to show/hide the cancel button.
   * @type {boolean}
   */
  public cancelButton: boolean;

  /**
   * Flag to show/hide percentage calculation display.
   * @type {boolean}
   */
  public percentageCal: boolean;

  /**
   * Flag to make the form read-only (e.g., after submission).
   * @type {boolean}
   */
  public formReadonly: boolean;

  /**
   * Number of animals in the source tank.
   * @type {number | any}
   */
  public sourceAnimals: number | any;

  /**
   * Number of animals in the destination tank.
   * @type {number | any}
   */
  public destinationAnimals: number | any;

  /**
   * Calculated percentage of animals being shifted.
   * @type {number}
   */
  public percentage: number;

  /**
   * Dropdown options for source ponds.
   * @type {Array<any>}
   */
  public source_pondsDetails_dropdown = new Array();

  /**
   * Dropdown options for destination ponds.
   * @type {Array<any>}
   */
  public destination_pondsDetails_dropdown = new Array();

  /**
   * Flag to show/hide the confirmation modal/section.
   * @type {boolean}
   */
  public confirmHideShow: boolean;

  /**
   * Application permissions object.
   * @type {any}
   */
  public appPermission: any;

  /**
   * Keyword used for filtering/searching.
   * @type {string}
   */
  public keyword: string;

  /**
   * Generic item property.
   * @type {any}
   */
  public item: any;

  /**
   * Reactive form group for initial selection (business, location).
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Reactive form group for shift details (sections, tanks, count).
   * @type {FormGroup}
   */
  public shiftDetailsForm: FormGroup;

  /**
   * Flag for business dropdown open/close state.
   * @type {boolean}
   * @default false
   */
  public isDropdownOpen = false;

  /**
   * Filtered options for business dropdown.
   * @type {any[]}
   */
  public filteredOptions: any[] = [];

  /**
   * Placeholder text for business search input.
   * @type {string}
   * @default 'Enter the business name'
   */
  public businessSearch: string = 'Enter the business name';

  /**
   * Flag indicating if the initial form has been submitted.
   * @type {boolean}
   */
  public submitted: boolean;

  /**
   * Flag indicating if the shift details form has been submitted.
   * @type {boolean}
   */
  public shiftDetailSubmitted: boolean;

  /**
   * Flag to disable the submit button during async operations.
   * @type {boolean}
   * @default false
   */
  public submitButtonDisabled: boolean = false;

  /**
   * Constructor for TankshiftComponent
   *
   * Initializes the component, injects required services, and sets up reactive forms.
   *
   * Form Validation Rules:
   * - registerForm: businessfield (required), locationField (required).
   * - shiftDetailsForm: fromsection (required), fromTankId (required), dessection (required), toTankId (required), count (required, numeric).
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {BusinessEntityService} businessEntityService - Service for Business Entity API operations.
   * @service BusinessEntityService
   * @param {FarmSiteService} farmSitesService - Service for Farm Site API operations.
   * @service FarmSiteService
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @service ActivatedRoute
   * @param {Router} router - Service for navigation.
   * @service Router
   * @param {CustomerPondService} customerPondService - Service for Customer Pond API operations.
   * @service CustomerPondService
   * @param {FeedLogService} feedLogService - Service for Feed Log (and Tank Shift) API operations.
   * @service FeedLogService
   * @param {PondSectionService} pondSectionService - Service for Pond Section API operations.
   * @service PondSectionService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public route: ActivatedRoute,
              public router: Router, public customerPondService: CustomerPondService, public feedLogService: FeedLogService, public pondSectionService: PondSectionService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.feedLog = new FeedLogModel();
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required]
    });
    this.shiftDetailsForm = this.formBuilder.group({
      fromsection: ['', Validators.required],
      fromTankId: ['', Validators.required],
      dessection: ['', Validators.required],
      toTankId: ['', Validators.required],
      count: ['', [Validators.required,
        Validators.pattern('^[0-9]+$')]],
    });
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Tracks the current route.
   * 2. Retrieves user details from local storage.
   * 3. Resets component state flags.
   * 4. Loads business and farm site data based on user role (Farmer vs Admin vs Provider).
   * 5. Auto-selects farm site if only one is available.
   * 6. Loads business entities for selection if user is an employee/admin.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.LoginUserName = localStorage.getItem('LoginUserName');
    this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
    this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
    /* user login details end */
    this.responseLoader = true;
    this.gotoButton = false;
    this.goToConfirm = false;
    this.disableDownload = false;
    this.isCommitDisabled = false;
    this.percentageCal = false;
    this.formReadonly = false;
    this.cancelButton = false;
    this.displayTanks = false;
    this.percentage = 0;
    this.confirmHideShow = false;
    this.keyword = 'businessName';
    try {
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.feedLog.farm_site_id = undefined;
        this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(localStorage.businessID);
        this.responseLoader = true;
        this.setSingleValueForFormLocation();
        this.feedLog.busienss_id = localStorage.businessID;
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.feedLog.farm_site_id = undefined;
        this.farmSites = await this.farmSitesService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        this.responseLoader = true;
        this.setSingleValueForFormLocation();
        this.feedLog.busienss_id = localStorage.businessID;
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.registerForm.get('businessfield').patchValue(this.loginUserBusinessName);
        this.feedLog.busienss_id = localStorage.businessID;
        this.farmSites = await this.farmSitesService.getFarmeSitesBusinessDetailsByProviderId(localStorage.businessID);
        this.setSingleValueForFormLocation();
      } else {
        this.businessSearch = undefined;
        this.registerForm.get('businessfield').patchValue('');
        let farmBusiness = await this.businessEntityService.getAllBusiness();
        this.businessEntities = farmBusiness.filter(businessEntity => businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID
          === '618916f2b4391c6d48137e3d');
        this.filteredOptions = this.businessEntities;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Getter for easy access to registerForm controls.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * Getter for easy access to shiftDetailsForm controls.
   *
   * @method f1
   * @returns {AbstractControl} Form controls
   */
  public get f1() {
    return this.shiftDetailsForm.controls;
  }

  /**
   * Filters business options based on search text.
   *
   * @method filterBusinesses
   * @returns {void}
   * @side-effect Updates filteredOptions array.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles business selection from dropdown.
   *
   * @method selectOption
   * @param {BusinessEntity} business - The selected business entity.
   * @returns {void}
   */
  public selectOption(business: BusinessEntity): void {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.feedLog.busienss_id = business.id;
    if (this.feedLog.busienss_id) {
      this.getAllFarmsitesBybusinessId();
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.pondSectionService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
  }

  /**
   * Fetches all farm sites for the selected business.
   *
   * - Resets tank selection fields.
   * - Calls `getFarmeSitesByFarmId` service method.
   *
   * @async
   * @method getAllFarmsitesBybusinessId
   * @returns {Promise<void>}
   */
  public async getAllFarmsitesBybusinessId() {
    try {
      this.displayTanks = false;
      this.feedLog.source_section_id = undefined;
      this.feedLog.source_tank_id = undefined;
      this.feedLog.destination_tank_id = undefined;
      this.feedLog.destination_section_id = undefined;
      this.feedLog.farm_site_id = undefined;
      this.feedLog.source_tank_id = undefined;
      this.displayTanks = false;
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.feedLog.busienss_id);
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Auto-selects farm site if only one is available.
   * Triggers loading of sections and ponds for the selected site.
   *
   * @method setSingleValueForFormLocation
   * @returns {void}
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.feedLog.farm_site_id = this.farmSites[0].id; // model-binding key
      this.getSetionsByFarmsiteId();
      this.getPondsByFarmsiteId();
    }
  }

  /**
   * Fetches pond sections for the selected farm site.
   *
   * @async
   * @method getSetionsByFarmsiteId
   * @returns {Promise<void>}
   */
  public async getSetionsByFarmsiteId() {
    try {
      this.pondSections = await this.pondSectionService.getPondSectionsByFarmSite(this.feedLog.farm_site_id);
      this.sourceSections = this.pondSections;
      this.destinationpondSections = this.pondSections;
      this.setSingleValueForSection();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Auto-selects section if only one is available in the list.
   *
   * @method setSingleValueForSection
   * @returns {void}
   */
  public setSingleValueForSection() {
    if (this.sourceSections && this.sourceSections.length == 1) {
      this.feedLog.source_section_id = this.sourceSections[0].id; // model-binding key
    }
    if (this.destinationpondSections && this.destinationpondSections.length == 1) {
      this.feedLog.destination_section_id = this.destinationpondSections[0].id; // model-binding key
    }
  }

  /**
   * Fetches ponds based on the selected section ID (Source or Destination).
   * Filters ponds to ensure they belong to the selected farm site and have active culture.
   *
   * @async
   * @method getPondsBySectionId
   * @param {string} type - 'From' (Source) or 'To' (Destination).
   * @returns {Promise<void>}
   */
  public async getPondsBySectionId(type: string) {
    this.responseLoader = false;
    let input;
    if (type == 'From') {
      input = this.feedLog.source_section_id;
      try {
        let customerPonds = await this.customerPondService.getAllPondsBySectionId(input);
        this.customerPonds = customerPonds.filter(pond => pond.farm_site_id == this.feedLog.farm_site_id);
        this.source_pondsDetails_dropdown = [];
        this.responseLoader = true;
        if (this.customerPonds && this.customerPonds.length > 0) {
          for (let i = 0; i < this.customerPonds.length; i++) {
            let eachrec = this.customerPonds[i];
            if (eachrec.isPreparatory == false && (eachrec.PondCultureData && eachrec.PondCultureData.length > 0)) {
              for (let j = 0; j < eachrec.PondCultureData.length; j++) {
                let eachpondculture = eachrec.PondCultureData[j];
                if (eachpondculture.end_date == null) {
                  eachpondculture.pond_name = eachrec.name;
                  eachpondculture.pond_id = eachrec.id;
                  eachpondculture.pond_size = eachrec.pond_size;
                  this.source_pondsDetails_dropdown.push(eachpondculture);
                }
              }
            }
          }
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
      }
    }
    if (type == 'To') {
      input = this.feedLog.destination_section_id;
      try {
        let descustomerPonds = await this.customerPondService.getAllPondsBySectionId(input);
        this.descustomerPonds = descustomerPonds.filter(pond => pond.farm_site_id == this.feedLog.farm_site_id);
        this.destination_pondsDetails_dropdown = [];
        this.responseLoader = true;
        if (this.descustomerPonds && this.descustomerPonds.length > 0) {
          for (let i = 0; i < this.descustomerPonds.length; i++) {
            let eachrec = this.descustomerPonds[i];
            if (eachrec.isPreparatory == false && (eachrec.PondCultureData && eachrec.PondCultureData.length > 0)) {
              for (let j = 0; j < eachrec.PondCultureData.length; j++) {
                let eachpondculture = eachrec.PondCultureData[j];
                if (eachpondculture.end_date == null) {
                  eachpondculture.pond_name = eachrec.name;
                  eachpondculture.pond_id = eachrec.id;
                  eachpondculture.pond_size = eachrec.pond_size;
                  this.destination_pondsDetails_dropdown.push(eachpondculture);
                }
              }
            }
          }
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
      }
    }
  }

  /**
   * Fetches all ponds for the selected farm site.
   * Populates both source and destination dropdowns with active ponds.
   *
   * @async
   * @method getPondsByFarmsiteId
   * @returns {Promise<void>}
   */
  public async getPondsByFarmsiteId() {
    this.responseLoader = false;
    try {
      this.siteBycustomerPonds = await this.customerPondService.getAllPondDetailsByFarmSiteId(this.feedLog.farm_site_id);
      this.responseLoader = true;
      this.source_pondsDetails_dropdown = [];
      this.destination_pondsDetails_dropdown = [];
      if (this.siteBycustomerPonds && this.siteBycustomerPonds.length > 0) {
        for (let i = 0; i < this.siteBycustomerPonds.length; i++) {
          let eachrec = this.siteBycustomerPonds[i];
          if (eachrec.isPreparatory == false && (eachrec.PondCultureData && eachrec.PondCultureData.length > 0)) {
            for (let j = 0; j < eachrec.PondCultureData.length; j++) {
              let eachpondculture = eachrec.PondCultureData[j];
              if (eachpondculture.end_date == null) {
                eachpondculture.pond_name = eachrec.name;
                eachpondculture.pond_id = eachrec.id;
                eachpondculture.pond_size = eachrec.pond_size;
                this.source_pondsDetails_dropdown.push(eachpondculture);
                this.destination_pondsDetails_dropdown.push(eachpondculture);
              }
            }
          }
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Hides the 'Go To' button.
   *
   * @method handelGotoButton
   * @returns {void}
   */
  public handelGotoButton() {
    this.gotoButton = false;
  }

  /**
   * Validates input to allow only numbers.
   *
   * @method numberValidation
   * @param {any} event - The keyboard event.
   * @returns {void}
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
}
  }

  /**
   * Advances to the next step (Tank Selection) after validating initial form.
   * Resets shift details and loads sections/ponds.
   *
   * @method goTo
   * @returns {void}
   */
  public goTo() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    this.shiftDetailsForm.markAsPristine(); // optional - clears dirty flags
    this.shiftDetailsForm.markAsUntouched(); // optional - clears touched state
    if (this.registerForm.invalid) {
      return;
    }
    this.feedLog.source_tank_id = undefined;
    this.feedLog.destination_tank_id = undefined;
    this.source_pondsDetails_dropdown = [];
    this.destination_pondsDetails_dropdown = [];
    this.sourceSections = [];
    this.destinationpondSections = [];
    this.feedLog.source_section_id = undefined;
    this.feedLog.destination_section_id = undefined;
    this.sourceAnimals = 0;
    this.destinationAnimals = 0;
    this.feedLog.shifted_animals = undefined;
    this.gotoButton = true;
    this.percentageCal = true;
    this.displayTanks = true;
    this.goToConfirm = true;
    this.getSetionsByFarmsiteId();
    this.getPondsByFarmsiteId();
  }

  /**
   * Calculates the percentage of animals being shifted from the source pond.
   * Validates that shifted count does not exceed source count.
   *
   * @method SelectedSourcePondPercentage
   * @returns {boolean | void} False if validation fails.
   */
  public SelectedSourcePondPercentage() {
    this.percentage = 0;
    if (this.sourceAnimals < this.feedLog.shifted_animals) {
      this.toastrService.warning('Shifted animals should be less than source tank animals')
      this.feedLog.shifted_animals = null;
      return false;
    }
    if (this.sourceAnimals && this.feedLog.shifted_animals) {
      const percentageCalculation = (this.feedLog.shifted_animals / this.sourceAnimals) * 100;
      this.percentage = Math.round(percentageCalculation);
    }
  }

  /**
   * Updates the count of animals in the selected source tank.
   *
   * @method selectedSourceTankAnimals
   * @returns {void}
   */
  public selectedSourceTankAnimals() {
    this.sourceAnimals = 0;
    const sourcePond = this.source_pondsDetails_dropdown.find(pondId => pondId && pondId.pond_id == this.feedLog.source_tank_id);
    this.sourceAnimals = sourcePond.present_no_of_animals;
  }

  /**
   * Updates the count of animals in the selected destination tank.
   *
   * @method selectedDestinationTankAnimals
   * @returns {void}
   */
  public selectedDestinationTankAnimals() {
    this.destinationAnimals = 0;
    const destinationPond = this.destination_pondsDetails_dropdown.find(pondId => pondId && pondId.pond_id == this.feedLog.destination_tank_id);
    this.destinationAnimals = destinationPond.present_no_of_animals;
  }

  /**
   * Executes the tank shift operation.
   *
   * - Validates shift details form.
   * - Checks animal counts.
   * - Prepares data payload for API.
   * - Calls `feedLogService.tankShift`.
   * - Handles success/failure responses.
   *
   * @async
   * @method shift
   * @returns {Promise<boolean | void>}
   */
  public async shift() {
    this.shiftDetailSubmitted = true;
    this.shiftDetailsForm.markAllAsTouched();
    if (this.shiftDetailsForm.invalid) {
      return;
    }
    try {
      this.cancelButton = false;
      this.confirmHideShow = true;
      this.submitButtonDisabled = true;
      const sourcepond = this.source_pondsDetails_dropdown.find(pondId => pondId && pondId.pond_id == this.feedLog.source_tank_id);
      const destinationitem = this.destination_pondsDetails_dropdown.find(pondId => pondId && pondId.pond_id == this.feedLog.destination_tank_id);
      if (this.sourceAnimals == 0) {
        this.toastrService.warning('There is no animals in source tank');
        this.confirmHideShow = false;
        return false;
      }
      const cultureUpdateData = {
        pond_culture_id: sourcepond.id,
        numberOfAnimals: sourcepond.present_no_of_animals,
        sourceTankCulture_StockingSize: sourcepond.stocking_size,
        sourceTankCulture_StockingCount: sourcepond.stocking_count,
        destinationTankCulture_StockingCount: destinationitem.stocking_count,
        destinationTankCulture_StockingSize: destinationitem.stocking_size,
        destinationTankCulture_NumberOfAnimals: destinationitem.present_no_of_animals,
        destinationTankCulture_PondSize: destinationitem.pond_size,
        sourceTankCulture_PondSize: sourcepond.pond_size,
        shifted_animals: this.feedLog.shifted_animals,
        destination_pond_culture_id: destinationitem.id
      };
      const inputData = {
        shift_date: moment(new Date()).format('YYYY-MM-DD'),
        source_tank_id: Number(this.feedLog.source_tank_id),
        pond_culture_id: sourcepond.id,
        farm_site_id: this.feedLog.farm_site_id,
        destination_pond_culture_id: destinationitem.id,
        destination_tank_id: Number(this.feedLog.destination_tank_id),
        numberOfAnimals: sourcepond.present_no_of_animals,
        shifted_animals: Number(this.feedLog.shifted_animals),
        cultureUpdateDetails: cultureUpdateData,
        person_id: localStorage.getItem('personID'),
        section_id: Number(this.feedLog.source_section_id)
      };
      if (this.sourceAnimals < this.feedLog.shifted_animals) {
        this.toastrService.warning('Shifted animals should be less than source tank animals');
        return false;
      } else if (this.sourceAnimals > this.feedLog.shifted_animals) {
        inputData['shiftType'] = 'Partial';
      } else if (this.sourceAnimals == this.feedLog.shifted_animals) {
        inputData['shiftType'] = 'Fully';
      }
      this.responseFeedLog = await this.feedLogService.tankShift(inputData);
      this.toastrService.success(this.responseFeedLog.cultureDetailsUpdateStatus.message, '', {
        timeOut: 4000
      });
      this.goToConfirm = false;
      this.formReadonly = true;
      this.cancelButton = true;
      this.submitted = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.router.navigate(['/tankshift']);
      this.confirmHideShow = true;
      await this.ngOnInit();
      this.confirmHideShow = false;
      this.submitButtonDisabled = false;
    }
  }

  /**
   * Resets the form and component state to default (Close/Cancel action).
   *
   * @method closeForm
   * @returns {void}
   */
  public closeForm() {
    this.ngOnInit();
    this.feedLog = new FeedLogModel();
    if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') {
      this.farmSites = [];
      this.feedLog.farm_site_id = undefined;
      this.item = undefined;
      this.shiftDetailSubmitted = false;
    }
    this.submitButtonDisabled = false;
    this.shiftDetailSubmitted = false;    // Reset validation flag
    this.shiftDetailsForm.markAsPristine(); // optional - clears dirty flags
    this.shiftDetailsForm.markAsUntouched(); // optional - clears touched state
    this.submitted = false;    // Reset validation flag
    this.registerForm.markAsPristine(); // optional - clears dirty flags
    this.registerForm.markAsUntouched(); // optional - clears touched state
  }
}
